import { Button, Card, CardBody, Col, Container, FormFeedback, FormGroup, FormText, Input, Label, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap";
import { useTheme } from "../../../hooks/useTheme";
import { useState, useEffect } from "react";
import { toast } from 'react-toastify';
import CustomSelect from "../../../components/CustomSelect";
import DialogBox from "../../../components/DialogBox";
import { createQuoteTemplate, updateQuoteTemplate, getAWSSettings, getPreSignedUrl } from "../data/quoteTemplateService";
import { createQuoteTemplateTag, deleteQuoteTemplateTags } from "../data/quoteTemplateTagService";
import { downloadFileFromUrl } from '../../../utils/fileUtils';
import { createAudit, fetchAuditByFilter } from "../../audit/data/auditService";
import DataTable from "../../../components/DataTable";
import { fetchUsers } from "../../users/data/userService";
import { useQuery } from "@tanstack/react-query";
import { formatDateTime } from "../../../utils/dateUtils";
import DocViewer, { MSDocRenderer } from '@cyntler/react-doc-viewer';
import './renderer.scss';
import AdminQuoteTemplateCustomSectionsTable from "./AdminQuoteTemplateCustomSectionsTable";
import CopyToClipboardButton from '../../../components/CopyToClipboardButton';

export default function AdminQuoteTemplateModal({ toggle, template: initialTemplate, tagsData, onTemplateChange, handleDelete }) {
    const theme = useTheme();
    // State for item (to update the display dynamically)
    const [template, setTemplate] = useState(structuredClone(initialTemplate));
    const [originalTemplate, setOriginalTemplate] = useState(structuredClone(initialTemplate));
    const [isDialogBoxOpen, setIsDialogBoxOpen] = useState(false);
    const [isDeleteDialogBoxOpen, setIsDeleteDialogBoxOpen] = useState(false);
    const [isSaveButtonVisible, setIsSaveButtonVisible] = useState(true);
    const [exportToS3, setExportToS3] = useState(false);
    const [url, setUrl] = useState('');

    useEffect(() => {
        if (template.s3Key) getUrl();
    }, [template]);

    const getUrl = async () => {
        const url = await getPreSignedUrl(template.s3Key);
        setUrl(url.presignedUrl);
    }

    const [currentActiveMainTab, setCurrentActiveMainTab] = useState("Details");

    useEffect(() => {
        const fetchAwsSettings = async () => {
            try {
                const settings = await getAWSSettings();
                setExportToS3(settings.uploadFile);
            } catch (error) {
                console.error("Error loading AWS settings:", error);
                setExportToS3(false);
            }
        }

        fetchAwsSettings();
    }, []);

    // Handle input changes
    const handleChange = async (e) => {
        const { name, value, type, checked } = e.target;
        setTemplate(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    }

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setTemplate(prev => ({ ...prev, s3Key: e.target.files[0].name, file: e.target.files[0] }));
    }

    const handleFileDownload = async (key) => {
        const url = await getPreSignedUrl(key);
        if (!url) return;
        if (url.errorMessage)
            toast.error("Download failed. " + url.errorMessage);
        else
            await downloadFileFromUrl(url.presignedUrl);
    };

    const handleTagChange = (selectedOptions) => {
        setTemplate({ ...template, tags: selectedOptions.map((tag) => ({ id: tag.value, tagName: tag.label })) })
    }

    const handleCustomSectionsChange = (next, config) => {
        setTemplate({ ...template, customSections: next, customSectionsConfig: config });
    }

    const saveChanges = async () => {
        if (JSON.stringify(template) === JSON.stringify(originalTemplate)) {
            toggle();
            return
        }
        if (!validateTemplate()) return false;

        setIsSaveButtonVisible(false);

        const isNew = template.id === 0
        let templateToSave = { ...template }
        let auditChanges = [];

        const saveTemplateIsOK = await saveTemplate(templateToSave, isNew, auditChanges);
        if (!saveTemplateIsOK) {
            setIsSaveButtonVisible(true);
            return;
        }

        await saveTemplateTags(templateToSave, isNew, auditChanges);
        await saveAudit(templateToSave, isNew, auditChanges);

        setTemplate(templateToSave);
        setOriginalTemplate(templateToSave); //update the original template in memory to compare with future changes
        onTemplateChange(templateToSave, isNew);
        toggle();

        setIsSaveButtonVisible(true);
    }

    const validateTemplate = () => {
        let blankFields = []
        if (!template.name) blankFields.push("Name");
        if (!template.description) blankFields.push("Description");
        if (blankFields.length > 0) {
            toast.warning(`Please fill in the following fields: ${blankFields.join(", ")}`);
            return false
        }

        if (template.id === 0) { // new template
            if (!template.file) {
                toast.warning('Please select a file.');
                return false;
            }
            if (template.file.size === 0) {
                toast.warning('The selected file is empty.');
                return false;
            }
        }

        return true
    }

    const saveTemplate = async (template, isNew, auditChanges) => {
        let cleanTemplate = { ...template };
        delete cleanTemplate.tags

        let cleanOriginalTemplate = { ...originalTemplate };
        delete cleanOriginalTemplate.tags

        if (JSON.stringify(cleanTemplate) !== JSON.stringify(cleanOriginalTemplate)) {
            if (isNew) {
                const formData = new FormData();
                formData.set("file", cleanTemplate.file);
                formData.set("name", cleanTemplate.name);
                formData.set("description", cleanTemplate.description);
                formData.set("disabled", cleanTemplate.disabled);
                formData.set("customSections", cleanTemplate.customSections ? JSON.stringify(cleanTemplate.customSections) : null);
                formData.set("customSectionsConfig", cleanTemplate.customSectionsConfig ? JSON.stringify(cleanTemplate.customSectionsConfig) : null);
                const response = await createQuoteTemplate(formData)
                if (response.newId) {
                    auditTemplateChanges(true, auditChanges)
                    template.id = response.newId
                    template.createdAt = response.createdAt;
                } else {
                    toast.warning(`Error: ${response}`);
                    return false
                }
            } else {                    // existing template
                auditTemplateChanges(false, auditChanges)
                delete cleanTemplate.s3Key

                if (process.env.REACT_APP_USELOCALDATA === "true") {
                    template.updatedAt = new Date().toISOString();
                }
                else {
                    cleanTemplate.customSections = cleanTemplate.customSections ? JSON.stringify(cleanTemplate.customSections) : null;
                    cleanTemplate.customSectionsConfig = cleanTemplate.customSectionsConfig ? JSON.stringify(cleanTemplate.customSectionsConfig) : null;
                    const response = await updateQuoteTemplate(cleanTemplate.id, cleanTemplate);
                    template.updatedAt = response.updatedAt
                }
            }
        } else { // if the template has not changed but the tags have, update the main template 'UpdatedAt' column
            if (JSON.stringify(template.tags) !== JSON.stringify(originalTemplate.tags)) {
                template.updatedAt = new Date().toISOString();
                await updateQuoteTemplate(template.id, template);
            }
        }
        return true
    }

    const saveTemplateTags = async (template, isNew, auditChanges) => {
        if (JSON.stringify(template.tags) !== JSON.stringify(originalTemplate.tags)) {
            await deleteQuoteTemplateTags(template.id);

            await Promise.all(
                template.tags.map(async (tag) =>
                    await createQuoteTemplateTag({ quoteTemplateId: template.id, tagId: tag.id })
                )
            );

            auditTagChanges(isNew, auditChanges)
        }
    }

    const auditTemplateChanges = (isNewTemplate, auditChanges) => {
        const auditFieldsToCompare = ["name", "description", "disabled", "s3Key"]
        auditFieldsToCompare.forEach((field) => {
            const originalField = JSON.stringify(originalTemplate[field])
            const currentField = JSON.stringify(template[field])

            if (!isNewTemplate) {
                if (currentField !== originalField) {
                    auditChanges.push(`\"${field}\": {\"new\": ${currentField}, \"old\": ${originalField}}`)
                }
            } else {
                auditChanges.push(`\"${field}\": ${currentField}`)
            }
        })
    }

    const auditTagChanges = (isNewTemplate, auditChanges) => {
        const field = "tags"
        const originalField = JSON.stringify(originalTemplate[field])
        const currentField = JSON.stringify(template[field])

        if (!isNewTemplate) {
            if (currentField !== originalField) {
                auditChanges.push(`\"${field}\": {\"new\": ${currentField}, \"old\": ${originalField}}`)
            }
        } else {
            auditChanges.push(`\"${field}\": ${JSON.stringify(template[field])}`)
        }
    }

    const saveAudit = async (template, isNewTemplate, auditChanges) => {
        if (auditChanges.length === 0) return

        const auditEntry = "{" + (auditChanges.join(", ")) + "}";
        const action = isNewTemplate ? "Quote template created" : "Quote template changed";
        await createAudit({ "action": action, "result": "Successful", "source": `quoteTemplate.${template.id}`, "payload": auditEntry })
    }

    const handleClose = () => {
        if (JSON.stringify(template) !== JSON.stringify(originalTemplate)) {
            setIsDialogBoxOpen(true);
        } else {
            toggle();
        }
    }

    const historyColumns = [
        {
            key: "timestamp",
            label: "Timestamp"
        },
        { key: "user", label: "User" },
        { key: "action", label: "Action" },
        { key: "notes", label: "Notes" },
        { key: "payload", label: "Payload" }
    ];

    const getUsersLookup = async () => {
        const users = await fetchUsers();
        return users.map(user => ({
            ...user,
            fullName: `${user.firstName} ${user.lastName}`
        }))
    }

    const {
        data: usersLookup,
        isLoading: isLoadingUsersLookup,
        error: usersLookupError,
    } = useQuery({
        queryKey: ["quoteTemplateUsers"],
        queryFn: getUsersLookup,
        staleTime: 60000
    });

    const getAudit = async () => {
        const filter = { "pageNumber": 0, "pageSize": 0, "source": `quoteTemplate.${template.id}` }
        const audits = await fetchAuditByFilter(filter);

        return audits.auditItems.map((audit) => {
            const user = usersLookup.find((user) => user?.userGuid === audit.userGuid);
            return {
                ...audit,
                timestamp: formatDateTime(audit.timestamp),
                user: user ? `${user.firstName} ${user.lastName}` : "Unknown",
            };
        });
    }

    const {
        data: auditData,
        isLoading: isLoadingAudits,
        error: auditError,
        refetch: refetchAudits,
    } = useQuery({
        queryKey: ["quoteTemplateAudits"],
        queryFn: getAudit,
        enabled: !!usersLookup, // Only run when usersLookup are available
        staleTime: 60000
    });

    useEffect(() => {
        if (usersLookup) {
            refetchAudits();
        }
    }, [usersLookup]);

    const docs = url && url !== ''
        ? [
            {
                uri: url,
                fileType: template.s3Key.split(".").pop(),
                name: template.s3Key,
            },
        ]
        : [];

    return (
        <>
            <ModalHeader toggle={handleClose}>
                {template.name || "Quote Template Details"}
            </ModalHeader>
            <ModalBody className="modal-body-flex bg p-3 d-flex flex-column">
                {/* <div className="bg p-3 d-flex flex-column" style={{ position: "absolute", inset: "0rem" }}> */}
                <Row className="flex-grow-1">
                    {/* Settings Area */}
                    <Col className="d-flex flex-column">
                        <Card className="mb-0 flex-grow-1">
                            <CardBody className="card-scrollable p-3">
                                <Row>
                                    <Col style={{ display: "inline-flex" }}>
                                        {["Details", "Custom Fields"].map((tab) => (
                                            <h6
                                                key={tab}
                                                className={
                                                    currentActiveMainTab === tab
                                                        ? "custom-nav-selected custom-nav m-3"
                                                        : "custom-nav m-3"
                                                }
                                                style={{ cursor: "pointer" }}
                                                onClick={() => setCurrentActiveMainTab(tab)}
                                            >
                                                {tab}
                                            </h6>
                                        ))}
                                    </Col>
                                    <Col className="text-end">
                                        {["Help", "History"].map((tab) => (
                                            <h6
                                                key={tab}
                                                className={
                                                    currentActiveMainTab === tab
                                                        ? "custom-nav-selected custom-nav m-3"
                                                        : "custom-nav m-3"
                                                }
                                                style={{ cursor: "pointer", display: 'inline-flex' }}
                                                onClick={() => setCurrentActiveMainTab(tab)}
                                            >
                                                {tab}
                                            </h6>
                                        ))}
                                    </Col>
                                </Row>
                                {currentActiveMainTab === "Details" && (
                                    <Row>
                                        <Container>
                                            <FormGroup row>
                                                <Label for="name" sm={2}>Name</Label>
                                                <Col sm={10}>
                                                    <Input
                                                        type="text"
                                                        name="name"
                                                        id="name"
                                                        value={template.name || ""}
                                                        onChange={handleChange}
                                                        invalid={!template.name}
                                                    />
                                                    <FormFeedback>Name is required.</FormFeedback>
                                                </Col>
                                            </FormGroup>
                                            <FormGroup row>
                                                <Label for="description" sm={2}>Description</Label>
                                                <Col sm={10}>
                                                    <Input
                                                        type="textarea"
                                                        name="description"
                                                        id="description"
                                                        value={template.description || ""}
                                                        onChange={handleChange}
                                                        invalid={!template.description}
                                                    />
                                                    <FormFeedback>Description is required.</FormFeedback>
                                                </Col>
                                            </FormGroup>
                                            <FormGroup row>
                                                <Label for="tags" sm={2}>Tags</Label>
                                                <Col sm={10}>
                                                    <CustomSelect
                                                        isMulti
                                                        creatable
                                                        options={
                                                            tagsData.map((tag) => ({
                                                                value: tag.id,
                                                                label: tag.tagName
                                                            }))
                                                        }
                                                        onChange={(selectedOptions) => handleTagChange(selectedOptions)}
                                                        defaultValue={template.tags.map((tag) => ({
                                                            value: tag.id,
                                                            label: tag.tagName
                                                        }))}
                                                    />
                                                </Col>
                                            </FormGroup>
                                            <FormGroup row>
                                                <Label for="disabled" sm={2}>Disabled</Label>
                                                <Col sm={10}>
                                                    <Input
                                                        type="checkbox"
                                                        name="disabled"
                                                        id="disabled"
                                                        checked={template.disabled}
                                                        value={template.disabled || false}
                                                        onChange={handleChange}
                                                    />
                                                </Col>
                                            </FormGroup>
                                            <hr />
                                            <FormGroup row>
                                                <Label for="file" sm={2}>
                                                    File
                                                </Label>
                                                <Col sm={10}>
                                                    {template.id === 0 ? (<Input
                                                        type="file"
                                                        name="file"
                                                        id="file"
                                                        accept=".docx"
                                                        onChange={handleFileChange}
                                                    />) : (
                                                        <>
                                                            <FormText name="file" id="file">{template.s3Key}</FormText>
                                                            <Button style={{ marginLeft: '8px' }} size="sm" onClick={() => handleFileDownload(template.s3Key)}>Download</Button>
                                                        </>
                                                    )}
                                                    <FormFeedback>File is required.</FormFeedback>
                                                </Col>
                                            </FormGroup>
                                            <FormGroup row>
                                                <Col sm={2}>
                                                </Col>
                                                <Col sm={10}>
                                                    {!exportToS3 && (<Label style={{ color: "#e29b28", fontSize: "0.85rem" }}>*configured not to save to S3</Label>)}
                                                </Col>
                                            </FormGroup>
                                        </Container>
                                    </Row>
                                )}
                                {currentActiveMainTab === "Custom Fields" && (
                                    <Row>
                                        <Container>
                                            <AdminQuoteTemplateCustomSectionsTable data={template.customSections} config={template.customSectionsConfig} onChange={handleCustomSectionsChange} />
                                        </Container>
                                    </Row>
                                )}
                                {currentActiveMainTab === "History" && (
                                    <DataTable
                                        className="mb-5 pb-5"
                                        columns={historyColumns}
                                        data={auditData ?? []}
                                        exportToCsv={true}
                                        csvFileName="audit_items.csv"
                                        sortByColumn="timestamp"
                                        sortDirection="descending"
                                        isLoading={isLoadingAudits || isLoadingUsersLookup}
                                        error={auditError || usersLookupError}
                                        searchableFields={["timestamp", "user", "action", "notes", "payload"]}
                                    />
                                )}
                                {currentActiveMainTab === "Help" && (
                                    <Container>
                                        <Table>
                                            <thead>
                                                <tr>
                                                    <th>Field</th>
                                                    <th>Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>{`{{OPPORTUNITY_ACCOUNT}}`}</td>
                                                    <td>The account on the opportunity.</td>
                                                    <td><CopyToClipboardButton text={`{{OPPORTUNITY_ACCOUNT}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{OPPORTUNITY_OWNER_NAME}}`}</td>
                                                    <td>The name of the opportunity owner.</td>
                                                    <td><CopyToClipboardButton text={`{{OPPORTUNITY_OWNER_NAME}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{OPPORTUNITYOWNER_TITLE}}`}</td>
                                                    <td>The title of the opportunity owner.</td>
                                                    <td><CopyToClipboardButton text={`{{OPPORTUNITYOWNER_TITLE}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{OPPORTUNITYOWNER_PHONE}}`}</td>
                                                    <td>The phone number of the opportunity owner.</td>
                                                    <td><CopyToClipboardButton text={`{{OPPORTUNITYOWNER_PHONE}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{OPPORTUNITYOWNER_MOBILE}}`}</td>
                                                    <td>The mobile number of the opportunity owner.</td>
                                                    <td><CopyToClipboardButton text={`{{OPPORTUNITYOWNER_MOBILE}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{OPPORTUNITYOWNER_EMAIL}}`}</td>
                                                    <td>The email address of the opportunity owner.</td>
                                                    <td><CopyToClipboardButton text={`{{OPPORTUNITYOWNER_EMAIL}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{OPPORTUNITY_ACCOUNT_NAME}}`}</td>
                                                    <td>Customer Name</td>
                                                    <td><CopyToClipboardButton text={`{{OPPORTUNITY_ACCOUNT_NAME}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{OPPORTUNITY_QUOTE_CONTACT__R}}`}</td>
                                                    <td>Customer Contact</td>
                                                    <td><CopyToClipboardButton text={`{{OPPORTUNITY_QUOTE_CONTACT__R}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{ACCOUNT_FULLBILLINGADDRESS}}`}</td>
                                                    <td>Invoice Address</td>
                                                    <td><CopyToClipboardButton text={`{{ACCOUNT_FULLBILLINGADDRESS}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{OPPORTUNITY_END_USER_CONTACT__R}}`}</td>
                                                    <td>End User / Site Contact</td>
                                                    <td><CopyToClipboardButton text={`{{OPPORTUNITY_END_USER_CONTACT__R}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{OPPORTUNITY_PRIMARY_DELIVERY_ADDRESS}}`}</td>
                                                    <td>Delivery Address</td>
                                                    <td><CopyToClipboardButton text={`{{OPPORTUNITY_PRIMARY_DELIVERY_ADDRESS}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{OPPORTUNITY_Customer_s_Ref}}`}</td>
                                                    <td>Customer Reference</td>
                                                    <td><CopyToClipboardButton text={`{{OPPORTUNITY_Customer_s_Ref}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{DP_NUMBER_REV}}`}</td>
                                                    <td>Quote Reference</td>
                                                    <td><CopyToClipboardButton text={`{{DP_NUMBER_REV}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{OPPORTUNITY}}`}</td>
                                                    <td>Salesforce Opportunity ID</td>
                                                    <td><CopyToClipboardButton text={`{{OPPORTUNITY}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{OPPORTUNITY_QUOTE_DESCRIPTION}}`}</td>
                                                    <td>The description of the opportunity quote.</td>
                                                    <td><CopyToClipboardButton text={`{{OPPORTUNITY_QUOTE_DESCRIPTION}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{QUOTE_ORDER_TABLE}}`}</td>
                                                    <td>The full quote order table including items, pricing, and totals.</td>
                                                    <td><CopyToClipboardButton text={`{{QUOTE_ORDER_TABLE}}`} /></td>
                                                </tr>
                                                <tr>
                                                    <td>{`{{TODAY}}`}</td>
                                                    <td>Today's date.</td>
                                                    <td><CopyToClipboardButton text={`{{TODAY}}`} /></td>
                                                </tr>
                                            </tbody>

                                        </Table>
                                    </Container>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                    {/* Preview Area */}
                    <Col className="">
                        <div style={{ width: '100%', height: '80vh', minHeight: '400px' }}>
                            {docs.length > 0 && url && url !== '' ?
                                <DocViewer
                                    pluginRenderers={[MSDocRenderer]}
                                    documents={docs}
                                    style={{ height: '100%' }}
                                />
                                :
                                <div className="h-100 d-flex align-items-center justify-content-center flex-column text-center">
                                    <i className="bi bi-file-earmark-text fs-1"></i>
                                    <p className="mt-2 mb-0">Preview not available</p>
                                </div>
                            }
                        </div>
                    </Col>
                </Row>
                {/* </div> */}
            </ModalBody>
            <ModalFooter style={{ justifyContent: 'space-between' }}>
                <div>
                    {template.id !== 0 && <Button color="danger" outline onClick={() => setIsDeleteDialogBoxOpen(true)}>Delete</Button>}
                </div>
                <div className="gap-2 d-flex">
                    <Button color={theme === "dark-mode" ? "dark" : "light"} outline onClick={handleClose}>Close</Button>
                    {isSaveButtonVisible &&
                        <Button onClick={saveChanges}>Save</Button>
                    }
                </div>
            </ModalFooter>

            {/* Remove confirmation dialog */}
            <DialogBox
                isOpen={isDeleteDialogBoxOpen}
                title="Are you sure you want to delete?"
                message={
                    <div className="d-flex align-items-center">
                        <i className="bi bi-x-circle-fill text-danger fs-1 me-3"></i>
                        <span>Do you want to remove this quote template?</span>
                    </div>
                }
                toggle={() => setIsDeleteDialogBoxOpen(false)}
                buttons={[
                    {
                        label: "No",
                        color: "secondary",
                        onClick: () => setIsDeleteDialogBoxOpen(false)
                    },
                    {
                        label: "Yes",
                        color: "primary",
                        onClick: () => handleDelete()
                    }
                ]}
            />

            <DialogBox
                isOpen={isDialogBoxOpen}
                title="Save Changes?"
                message={
                    <div className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill text-warning fs-1 me-3"></i>
                        <span>You have unsaved changes. <br />Save changes before closing?</span>
                    </div>
                }
                toggle={() => setIsDialogBoxOpen(false)}
                buttons={[
                    {
                        label: 'Yes',
                        color: 'primary',
                        // icon: 'bi bi-check2-circle',
                        onClick: () => { saveChanges(); setIsDialogBoxOpen(false); }
                    },
                    {
                        label: 'No',
                        color: 'secondary',
                        // icon: 'bi bi-x-circle',
                        onClick: () => { setIsDialogBoxOpen(false); toggle(); }
                    },
                    {
                        label: 'Cancel',
                        color: 'secondary',
                        onClick: () => { setIsDialogBoxOpen(false); }
                    }
                ]}
            />
        </>
    );
}